import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Check } from 'react-feather';
import { useHistory } from 'react-router-dom';
import { Button } from 'reactstrap';
import * as Sentry from '@sentry/browser';
import { Box } from '@bottomless/common/components';
import { useQueryString, useVendorLock } from '@bottomless/common/hooks';
import { withMetaTags } from '../../../components/MetaTags/MetaTags';
import { getPortalRedirectAction } from '../../../store';
import { ScaleConnection } from '../../../components/ScaleConnection';
import './ConnectSuccessPage.scss';
import { DefaultFaq } from './components/DefaultFaq';

const ConnectSuccessPageComponent = ({ getPortalRedirect }) => {
  const history = useHistory();
  const vendor = useVendorLock();
  const [isRedirecting, setRedirecting] = useState(false);

  const { params } = useQueryString();
  const isTare = useMemo(() => params.flow === 'tare', [params]);

  const onClick = useCallback(
    async e => {
      e.preventDefault();

      if (!vendor) {
        return history.push('/profile');
      }

      try {
        const { payload } = await getPortalRedirect();

        window.location.href = payload.url;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        Sentry.captureException(e);
      }

      setRedirecting(true);
    },
    [vendor, history, getPortalRedirect]
  );

  return (
    <div className="page-activate-success">
      <h1 className="text-center mb-4">Your Scale is Ready to Go!</h1>
      <Box secondary className="mb-4">
        <div className="d-flex justify-content-center mb-4">
          <span className="text-white success-checkmark d-flex align-items-center justify-content-center rounded-circle">
            <Check className="mt-1" />
          </span>
        </div>
        <h2 className="text-center mb-4">{isTare ? 'New "zero level" recorded.' : 'Scale successfully linked.'}</h2>

        {!isTare && (
          <>
            <div className="text-center mb-4">Place your product on the scale to begin tracking.</div>
            <ScaleConnection lines={[true, true, true, true]} />
          </>
        )}
        {isTare && (
          <>
            <div className="text-center text-secondary pt-3">Your new weight is:</div>
            <div className="success-checkmark-text display-4 text-center mb-4">
              0.0<span className="text-ounces">oz</span>
            </div>
            <div className="text-center">Place your product on the scale to begin tracking.</div>
          </>
        )}
      </Box>
      {!isTare && (
        <div className="mb-4 text-center">
          To manage your subscription, view your order history, or adjust settings, proceed to your profile.
        </div>
      )}
      <Button size="lg" color="dark" block className="mb-4" onClick={onClick}>
        {isRedirecting ? 'Redirecting...' : 'Go to Profile'}
      </Button>
      <DefaultFaq />
    </div>
  );
};

ConnectSuccessPageComponent.propTypes = {
  getPortalRedirect: PropTypes.func.isRequired,
};

export const ConnectSuccessPage = withMetaTags({
  title: 'Bottomless.com: Activate your smart scale',
})(
  connect(null, dispatch => ({
    getPortalRedirect: data => dispatch(getPortalRedirectAction(data)),
  }))(ConnectSuccessPageComponent)
);
